import cn from 'classnames';
import {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import SpinnerLoading from '../../../components/SpinnerLoading';
import useDeleteBasket from '../../../services/hookService/deleteBasket/useDeleteBasket';
import usePatchBasket from '../../../services/hookService/patchBasket/usePatchBasket';
import usePostBasket from '../../../services/hookService/postBasket/usePostBasket';
import DotsLoading from '../../../components/dotsLoading';

interface IProps {
  variant?: 'lg' | 'md' | 'sm';
  hideToast?: boolean;
  min_sell_quantity: number;
  available_quantity: number;
  hold_quantity: number;
  basket_quantity: number;
  basket_guid: string;
  advertisement_id: string;
  className?: string;
  handleRemovingItem?: any;
  shouldRemoveItem?: boolean;
  price?: number | null;
}

const BasketItemEditButton = ({
  variant = 'lg',
  hideToast = false,
  min_sell_quantity,
  available_quantity,
  hold_quantity,
  basket_quantity,
  basket_guid,
  advertisement_id,
  className = '',
  handleRemovingItem,
  shouldRemoveItem = false,
  price,
}: IProps) => {
  const navigate = useNavigate();

  const [count, setCount] = useState(basket_quantity);

  const initialQuantityRef = useRef(basket_quantity);
  const pendingQuantityRef = useRef(count);
  const lastActionRef = useRef<'increase' | 'decrease' | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const handleAfterDelete = () => {
    setCount(0);
    pendingQuantityRef.current = 0;
    initialQuantityRef.current = 0;
  };

  const {mutate: deleteBasketItem, isLoading: isDeleteLoading} =
    useDeleteBasket(handleAfterDelete);

  const {mutate: createBasket, isLoading: isCreating} = usePostBasket(() =>
    handleSuccess(min_sell_quantity),
  );

  const {mutate: editBasket, isLoading: isEditing} = usePatchBasket(
    ({quantity}: {quantity: number}) => handleSuccess(quantity),
  );

  const isLoading = isCreating || isEditing || isDeleteLoading;

  const trueQuantityAvailable =
    Math.abs((available_quantity ?? 0) - (hold_quantity ?? 0)) -
    pendingQuantityRef.current;

  const canIncrease =
    trueQuantityAvailable > 0 &&
    min_sell_quantity <= trueQuantityAvailable &&
    Boolean(price);

  const canDecrease =
    pendingQuantityRef.current > min_sell_quantity && Boolean(price);

  const trueMaxQuantity = Math.abs(
    (hold_quantity ?? 0) - (available_quantity ?? 0),
  );

  const hasMinSellQuantityIncrease = count < min_sell_quantity;
  const hasQuantityUpdated = trueMaxQuantity < count;

  useEffect(() => {
    const safe = basket_quantity ?? 0;

    setCount(safe);
    pendingQuantityRef.current = safe;
    initialQuantityRef.current = safe;
  }, [basket_quantity, basket_guid]);

  useEffect(() => {
    if (!basket_guid) return;

    if (hasMinSellQuantityIncrease) {
      setCount(min_sell_quantity);
      editBasket({quantity: min_sell_quantity, basket_guid});
    }

    if (hasQuantityUpdated) {
      setCount(trueMaxQuantity);
      editBasket({quantity: trueMaxQuantity, basket_guid});
    }
  }, [basket_guid]);

  const handleSuccess = (newQuantity: number) => {
    pendingQuantityRef.current = newQuantity;
    initialQuantityRef.current = newQuantity;
    setCount(newQuantity);

    const isIncrease = lastActionRef.current === 'increase';

    !hideToast &&
      toast.success(
        <div className="flex items-center justify-between px-2 text-sm">
          <span className={isIncrease ? 'text-lime-600' : 'text-red-600'}>
            {isIncrease
              ? 'محصول به سبد خرید اضافه شد'
              : 'محصول از سبد خرید کم شد'}
          </span>
          <div
            onClick={() => navigate('/shopping-basket')}
            className="px-2 py-2 text-white rounded-lg bg-lime-600"
          >
            رفتن به سبد خرید
          </div>
        </div>,
      );
  };

  const triggerDebouncedApi = () => {
    if (timerRef.current) clearTimeout(timerRef.current);

    timerRef.current = setTimeout(() => {
      const next = pendingQuantityRef.current;

      if (next === initialQuantityRef.current) return;

      if (!basket_guid) {
        createBasket({
          advertisement_id,
          quantity: min_sell_quantity,
        });
        return;
      }

      editBasket({
        quantity: next,
        basket_guid,
      });
    }, 1000);
  };

  const onAction = (action: 'increase' | 'decrease') => {
    lastActionRef.current = action;

    const next =
      action === 'increase'
        ? pendingQuantityRef.current + 1
        : Math.max(1, pendingQuantityRef.current - 1);

    pendingQuantityRef.current = next;
    setCount(next);

    triggerDebouncedApi();
  };

  const increase = () => {
    if (!canIncrease || isLoading) return;
    onAction('increase');
  };

  const decrease = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();

    if (isLoading) return;

    if (!canDecrease) {
      if (shouldRemoveItem) {
        handleRemovingItem(true);
      } else if (basket_guid) {
        deleteBasketItem(basket_guid);
      }
      return;
    }

    onAction('decrease');
  };

  return (
    <div
      className={cn(
        'flex items-center justify-between text-xl cursor-pointer z-20',
        {
          'w-24 h-8': variant === 'sm',
          'w-18 h-10': variant === 'md',
          'w-full h-12': variant === 'lg',
        },
        className,
      )}
    >
      <div
        className={cn(
          'bg-farm-shop-500 text-white w-full h-full flex justify-center items-center gap-2 rounded-xl px-2 relative',
          {'pointer-events-none': isLoading},
        )}
      >
        {/* content stays stable */}
        <div
          className={cn(
            'flex  gap-2 items-center justify-between w-full  h-full',
            {
              'opacity-60': isLoading,
            },
          )}
        >
          {count ? (
            <>
              <i
                className={cn('icon-namePlus-4 w-fit rounded-full', {
                  'pointer-events-none text-gray-400': !canIncrease,
                  'p-1': variant === 'sm',
                  'p-2': variant === 'md' || variant === 'lg',
                })}
                onClick={increase}
              />

              {isLoading ? (
                <div className="absolute inset-0 flex items-center justify-center rounded-lg bg-black/10">
                  {/* <SpinnerLoading size={12} /> */}
                  <DotsLoading small={!(variant === 'lg')} />
                </div>
              ) : (
                <span className="flex items-center justify-center w-full">
                  {count}
                </span>
              )}

              <i
                className={cn('icon-nameMinus-4 w-fit rounded-full', {
                  'p-1': variant === 'sm',
                  'p-2': variant === 'md' || variant === 'lg',
                })}
                onClick={e => decrease(e)}
              />
            </>
          ) : (
            <div
              onClick={increase}
              className={`${
                !canIncrease && 'pointer-events-none text-gray-400'
              } focus:scale-90 w-full  flex items-center justify-center gap-1 leading-none`}
            >
              <i className="icon-namePlus-4 text-[28px] flex items-center justify-center" />
              <i className="icon-nameBuy text-[28px] flex items-center justify-center" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BasketItemEditButton;
